import { FilesIcon } from 'lucide-react'
import pluralize from 'pluralize'
import { useMemo } from 'react'

import { Tooltip } from '@/components/ui'
import { Tally, useStatements } from '@/modules/projects'
import { getStatusDotClass } from '@/modules/projects/status'
import { cn, humanizeText } from '@/utils'
import { Progress } from './Progress'
import {
  describePhase,
  isEveryPageSucceeded,
  PAGE_PHASES,
  type PagePhase,
  tallyPagesByPhase,
} from './pagePhases'

const phaseLabel = (phase: PagePhase) => humanizeText(phase).toLowerCase()

interface ProjectStateBarProps {
  className?: string
  alwaysVisible?: boolean
  fullWidth?: boolean
  compact?: boolean
}

export const ProjectStateBar = ({
  className,
  alwaysVisible = false,
  fullWidth = false,
  compact = false,
}: ProjectStateBarProps) => {
  const statements = useStatements()
  const tally = useMemo(() => tallyPagesByPhase(statements), [statements])
  const { statementCount, totalPages, phases } = tally

  const isDone = isEveryPageSucceeded(tally)
  const showProgress = alwaysVisible || !isDone

  const legendPhases = PAGE_PHASES.filter((phase) => phases[phase] > 0)

  return (
    <div
      className={cn(
        !compact && 'rounded-lg border bg-white p-6',
        !compact && !fullWidth && 'xl:max-w-max',
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        {showProgress && (
          <div
            role="progressbar"
            aria-label="Pages processed"
            aria-valuemin={0}
            aria-valuemax={totalPages}
            aria-valuenow={phases.succeeded}
            className="bg-muted flex h-2 overflow-hidden rounded-sm"
          >
            {PAGE_PHASES.map((phase) => (
              <Progress
                key={`progress-${phase}`}
                value={phases[phase]}
                max={totalPages}
                status={phase}
                title={`${humanizeText(phase)}: ${describePhase(tally, phase)}`}
              />
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm">
          {!compact && (
            <div className="flex items-center gap-2">
              <FilesIcon className="size-4 text-muted-foreground" />
              <span className="font-semibold tabular-nums text-foreground">
                {totalPages.toLocaleString()}
              </span>
              <span className="text-muted-foreground">
                {pluralize('page', totalPages)} uploaded across{' '}
                <span className="font-medium tabular-nums text-foreground">
                  {statementCount.toLocaleString()}
                </span>{' '}
                {pluralize('statement', statementCount)}
              </span>
            </div>
          )}

          {legendPhases.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {legendPhases.map((phase) => (
                <Tooltip
                  key={`tally-${phase}`}
                  content={describePhase(tally, phase)}
                >
                  <div>
                    <Tally
                      label={phaseLabel(phase)}
                      count={phases[phase]}
                      dotClassName={getStatusDotClass(phase)}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
